import { Appointment, AppointmentWithPractitioner } from "@/src/models/appointment";
import { Referral, PaginatedReferral, ReferralStatus } from "@/src/models/documents/referral";
import { File } from "@/src/models/file";
import { PatientEngagement } from "@/src/models/patient-engagement";
import { SchedulingRecommendation, VerificationReason } from "@/src/models/scheduling-recommendation";
import { PaginatedReferralFiltersParams, ReferralFacetCountsParams } from "@/src/repo/referral";
import { fetchJSON } from "@/src/requests/utils/fetch-json";

export async function getAllReferrals(): Promise<Referral[]> {
  return fetchJSON<Referral[]>("/api/referrals");
}

export async function getReferralById(referralId: string): Promise<Referral> {
  return fetchJSON<Referral>(`/api/referrals/${referralId}`);
}

export async function getAllPatientReferrals(patientId: string): Promise<Referral[]> {
  return fetchJSON<Referral[]>(`/api/patients/${patientId}/referrals`);
}

export async function getLatestPatientReferral(patientId: string): Promise<Referral | null> {
  return fetchJSON<Referral | null>(`/api/patients/${patientId}/referrals/latest`);
}

export interface PaginatedReferralsResponse {
  referrals: PaginatedReferral[];
  availableDepartments: { id: string; label: string }[];
  totalCount: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
}

export async function getPaginatedReferrals(
  params: PaginatedReferralFiltersParams
): Promise<PaginatedReferralsResponse> {
  const {
    page = 0,
    pageSize = 30,
    schedulingPriority,
    status,
    dateFrom,
    dateTo,
    referringProvider,
    receivingProvider,
    receivingLocation,
    department,
    diagnosisCode,
    engagementOutcome,
    sortBy,
    sortOrder,
    query,
    syncedToEhr,
  } = params;

  const searchParams = new URLSearchParams({
    page: page.toString(),
    pageSize: pageSize.toString(),
  });

  if (schedulingPriority && schedulingPriority.length > 0) {
    searchParams.set("schedulingPriority", schedulingPriority.join(","));
  }

  if (status && status.length > 0) {
    searchParams.set("status", status.join(","));
  }

  if (dateFrom) {
    searchParams.set("dateFrom", dateFrom);
  }

  if (dateTo) {
    searchParams.set("dateTo", dateTo);
  }

  if (referringProvider && referringProvider.length > 0) {
    searchParams.set("referringProvider", referringProvider.join(","));
  }

  if (receivingProvider && receivingProvider.length > 0) {
    searchParams.set("receivingProvider", receivingProvider.join(","));
  }

  if (receivingLocation && receivingLocation.length > 0) {
    searchParams.set("receivingLocation", receivingLocation.join(","));
  }

  if (department && department.length > 0) {
    searchParams.set("department", department.join(","));
  }

  if (diagnosisCode && diagnosisCode.length > 0) {
    searchParams.set("diagnosisCode", diagnosisCode.join(","));
  }

  if (engagementOutcome && engagementOutcome.length > 0) {
    searchParams.set("engagementOutcome", engagementOutcome.join(","));
  }

  if (syncedToEhr === true) {
    searchParams.set("syncedToEhr", "true");
  }

  if (sortBy) {
    searchParams.set("sortBy", sortBy);
  }

  if (sortOrder) {
    searchParams.set("sortOrder", sortOrder);
  }

  if (query) {
    searchParams.set("query", query);
  }

  return fetchJSON<PaginatedReferralsResponse>(`/api/referrals/paginated?${searchParams.toString()}`);
}

export interface ReferralFacetCounts {
  schedulingPriority: Record<string, number>;
  status: Record<string, number>;
  referringProvider: Record<string, number>;
  receivingProvider: Record<string, number>;
  diagnosisCode: Record<string, number>;
}

export async function getReferralFacetCounts(params: ReferralFacetCountsParams): Promise<ReferralFacetCounts> {
  const { schedulingPriority, status, referringProvider, receivingProvider, department, diagnosisCode, query } = params;

  const searchParams = new URLSearchParams();

  if (schedulingPriority && schedulingPriority.length > 0) {
    searchParams.set("schedulingPriority", schedulingPriority.join(","));
  }

  if (status && status.length > 0) {
    searchParams.set("status", status.join(","));
  }

  if (referringProvider && referringProvider.length > 0) {
    searchParams.set("referringProvider", referringProvider.join(","));
  }

  if (receivingProvider && receivingProvider.length > 0) {
    searchParams.set("receivingProvider", receivingProvider.join(","));
  }

  if (department && department.length > 0) {
    searchParams.set("department", department.join(","));
  }

  if (diagnosisCode && diagnosisCode.length > 0) {
    searchParams.set("diagnosisCode", diagnosisCode.join(","));
  }

  if (query) {
    searchParams.set("query", query);
  }

  const queryString = searchParams.toString();

  const url = queryString ? `/api/referrals/facet-counts?${queryString}` : "/api/referrals/facet-counts";

  return fetchJSON<ReferralFacetCounts>(url);
}

export async function getReferralSchedulingRecommendation(
  referralId: string
): Promise<SchedulingRecommendation | null> {
  return fetchJSON<SchedulingRecommendation | null>(`/api/referrals/${referralId}/scheduling-recommendation`);
}

export interface VerifySchedulingRecommendationPayload {
  isCorrect: boolean | null;
  // Required when isCorrect === false, null otherwise (see PATCH route validation).
  verificationReason: VerificationReason | null;
}

export async function verifyReferralSchedulingRecommendation(
  referralId: string,
  payload: VerifySchedulingRecommendationPayload
): Promise<SchedulingRecommendation> {
  return fetchJSON<SchedulingRecommendation>(`/api/referrals/${referralId}/scheduling-recommendation`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function updateReferralStatus(
  referralId: string,
  status: ReferralStatus,
  // Note to fax on a close-out; null = no note.
  closeOutNote: string | null,
  // Appointment to fax on an appointment-booked transition (from the confirm dialog); null = auto-select.
  appointmentId: string | null,
  skipReferralUpdateFax?: boolean
): Promise<Referral> {
  return fetchJSON<Referral>(`/api/referrals/${referralId}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status, closeOutNote, appointmentId, skipReferralUpdateFax }),
  });
}

export async function getReferralAppointments(referralId: string): Promise<Appointment[]> {
  return fetchJSON<Appointment[]>(`/api/referrals/${referralId}/appointments`);
}

export async function getReferralUpcomingAppointments(referralId: string): Promise<AppointmentWithPractitioner[]> {
  return fetchJSON<AppointmentWithPractitioner[]>(`/api/referrals/${referralId}/upcoming-appointments`);
}

export async function getReferralEngagements(referralId: string): Promise<PatientEngagement[]> {
  return fetchJSON<PatientEngagement[]>(`/api/referrals/${referralId}/engagements`);
}

export async function getReferralFaxes(referralId: string): Promise<File[]> {
  return fetchJSON<File[]>(`/api/referrals/${referralId}/faxes`);
}

export async function updateReferralNote(referralId: string, note: string | null): Promise<Referral> {
  return fetchJSON<Referral>(`/api/referrals/${referralId}/note`, {
    method: "PATCH",
    body: JSON.stringify({ note }),
  });
}
